import { useSelector } from '@xstate/react';
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { UserContext } from '~/app';
import {
  AsyncSelect,
  Button,
  DatePicker,
  FormActions,
  FormLayout,
  FormSection,
  Multiselect,
  Select,
  TextInput,
} from '~/components';
import { findUsStateOption, US_STATES_AND_TERRITORIES } from '~/constants';
import { useFeatureFlag, useFeatureFlagStatus } from '~/features/feature-flags/useFeatureFlag';
import type { InquirySource } from '~/features/market/initialInquiry.machine';
import { loadAOIOptionByProviderCategory } from '~/features/market/listings/msaas-listing/wizard-steps/basic-info/utils';
import {
  ServiceDiscoveryBeginFlow,
  ServiceResponseBoundary,
} from '~/features/market/service-opportunities/ServiceResponseBoundary';
import {
  MIN_LOCATION_SEARCH_CHARS,
  searchGeographies,
} from '~/features/provider-hlx/service-listings/geographyApi';
import {
  formatKind,
  formatWorkLocation,
} from '~/features/provider-hlx/service-listings/providerServiceListingForm';
import type { ListingKind } from '~/features/provider-hlx/service-listings/types';
import { InitialInquiryFlow } from '~/features/shared/market-content/InitialInquiryFlow';
import { selectUnmaskViewerScope } from '~/features/shared/market-content/ResponseUnmaskPanel';
import { TutorialVideoButton } from '~/features/tutorials/TutorialVideoButton';
import type { OptionType } from '~/features/types';
import { extractBackendErrorDetail } from '~/features/utils';
import { loadCategoryOptions } from '~/features/vault/profile/api';
import { useCustomForm } from '~/hooks/useCustomForm';
import { paths } from '~/routing/routes';
import { buildProviderViewUrl, buildServiceOpportunitiesUrl } from '~/routing/urls';
import {
  type ListingKindFilter,
  loadSubspecialtyOptions,
  type ProviderSearchFilters,
  searchProviderServiceListings,
  type SubspecialtyOption,
} from './api';
import {
  autoSelectedStartKeys,
  earliestDateOptions,
  formatDate,
  isStartDateOnly,
  selectionOptionKey,
} from './availabilityOptionUtils';
import {
  availabilityWindowError,
  CARD_DATE_LIMIT,
  defaultEndDate,
  initialAvailabilityWindow as initialFilters,
  localDate,
  ProviderAvailabilityOptions,
  selectedAvailabilityEntryIds,
  StartOptionChoice,
} from './ProviderAvailabilityOptions';
import type {
  ProviderServiceListingSearchResponse,
  PublicAvailabilitySelectionOption,
} from './schemas';
import {
  clearSearch,
  type LocationOption,
  type ProviderSearchFormValues,
  readSearch,
  writeSearch,
} from './searchState';
export { selectedAvailabilityEntryIds, selectionOptionKey } from './ProviderAvailabilityOptions';

const PAGE_SIZE = 20;

// 'Custom' shifts are not a searchable choice: they still match an "Any" search.
const SHIFT_LABEL_OPTIONS: readonly OptionType[] = [
  { label: 'Any', value: '' },
  { label: 'Day', value: 'DAY' },
  { label: 'Evening', value: 'EVENING' },
  { label: 'Night', value: 'NIGHT' },
];

// No "Any": the search defaults to Locums, and End date applies to Locums only.
const EMPLOYMENT_TYPE_OPTIONS: readonly OptionType[] = [
  { label: 'Locums', value: 'LOCUMS' },
  { label: 'Part-time', value: 'PART_TIME' },
  { label: 'Full-time', value: 'FULL_TIME' },
];

const SHIFT_TYPE_OPTIONS: readonly OptionType[] = [
  { label: 'Any type', value: '' },
  { label: 'Active shift', value: 'SHIFT' },
  { label: 'On call', value: 'CALL' },
];

const WORK_LOCATION_OPTIONS: readonly OptionType[] = [
  { label: 'Any location', value: '' },
  { label: 'On-site', value: 'ONSITE' },
  { label: 'Remote', value: 'REMOTE' },
  { label: 'Hybrid', value: 'PARTIAL_REMOTE' },
];

/**
 * [MCV-1863] What the location menu says when it has no options: the picker waits for two
 * characters (a ZIP or city prefix), and a failed lookup is not the same as a place that doesn't exist.
 */
type LocationLookupStatus = 'short' | 'no-match' | 'error';

const LOCATION_MESSAGES: Record<LocationLookupStatus, string> = {
  short: `Type at least ${MIN_LOCATION_SEARCH_CHARS} characters of a city or ZIP code`,
  'no-match': 'No matching city or ZIP',
  error: "Couldn't look up locations. Try again.",
};

const lookupLocations = async (
  input: string,
  setStatus: (status: LocationLookupStatus) => void
): Promise<LocationOption[]> => {
  if (input.trim().length < MIN_LOCATION_SEARCH_CHARS) {
    setStatus('short');
    return [];
  }
  try {
    const options = (await searchGeographies(input)).map((geography) => ({
      value: String(geography.id),
      label: geography.label,
      latitude: Number(geography.latitude),
      longitude: Number(geography.longitude),
    }));
    setStatus(options.length ? 'short' : 'no-match');
    return options;
  } catch {
    setStatus('error');
    return [];
  }
};

/** [MCV-1863] A location only means something for work done in person. */
const NEAR_WORK_LOCATIONS = new Set(['ONSITE', 'PARTIAL_REMOTE']);
const takesLocation = (workLocation: OptionType | undefined) =>
  NEAR_WORK_LOCATIONS.has(String(workLocation?.value ?? ''));

const optionFor = (options: readonly OptionType[], value: string | undefined) =>
  options.find((option) => option.value === (value ?? '')) ?? options[0];

/** The first search every visit runs: the default employment type is Locums. */
const initialSearchFilters = (): ProviderSearchFilters => ({
  ...initialFilters(),
  listingKind: 'LOCUMS',
});

const toFormValues = (filters: ProviderSearchFilters): ProviderSearchFormValues => ({
  query: filters.query ?? '',
  employmentType: optionFor(EMPLOYMENT_TYPE_OPTIONS, filters.listingKind ?? 'LOCUMS'),
  providerCategory: null,
  specialty: null,
  subspecialties: [],
  start: filters.start,
  end: filters.end ?? '',
  shiftLabel: optionFor(SHIFT_LABEL_OPTIONS, filters.shiftLabel),
  shiftType: optionFor(SHIFT_TYPE_OPTIONS, filters.shiftType),
  workLocation: optionFor(WORK_LOCATION_OPTIONS, filters.workLocation),
  location: null,
  stateCodes: (filters.stateCodes ?? []).flatMap((code) => findUsStateOption(code) ?? []),
});

const isLocums = (employmentType: OptionType | undefined) =>
  (employmentType?.value ?? 'LOCUMS') === 'LOCUMS';

const toFilters = (values: ProviderSearchFormValues): ProviderSearchFilters => ({
  start: values.start,
  // End date is greyed out for anything but Locums, so it is not sent.
  end: isLocums(values.employmentType) ? values.end : undefined,
  query: values.query || undefined,
  listingKind: (values.employmentType?.value as ListingKindFilter | undefined) ?? 'LOCUMS',
  providerCategoryId: values.providerCategory ? Number(values.providerCategory.value) : undefined,
  specialtyId: values.specialty ? Number(values.specialty.value) : undefined,
  subspecialtyIds: values.subspecialties?.length
    ? [...new Set(values.subspecialties.map((option) => Number(option.value)))]
    : undefined,
  shiftLabel: values.shiftLabel?.value || undefined,
  shiftType: values.shiftType?.value || undefined,
  workLocation: values.workLocation?.value || undefined,
  // Sent only for On-site and Hybrid searches; the provider's own travel radius decides the match.
  ...(values.location && takesLocation(values.workLocation)
    ? { nearLat: values.location.latitude, nearLng: values.location.longitude }
    : {}),
  // [MCV-1950] A listing matches when it qualifies in any of the picked license states.
  stateCodes: values.stateCodes?.length
    ? [...new Set(values.stateCodes.map((option) => String(option.value)))]
    : undefined,
});

// The API explains a rejected search in `detail`; axios's own message is only
// "Request failed with status code 400".
const searchErrorMessage = (error: unknown) => {
  const detail =
    error != null && typeof error === 'object' && 'response' in error
      ? (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail
      : undefined;
  return extractBackendErrorDetail(detail) ?? 'Unable to search providers. Please try again.';
};

const SearchFilters = ({
  defaultValues,
  onApply,
  onClear,
}: {
  defaultValues: ProviderSearchFormValues;
  onApply: (filters: ProviderSearchFilters, values: ProviderSearchFormValues) => void;
  onClear: (filters: ProviderSearchFilters) => void;
}) => {
  const methods = useCustomForm<ProviderSearchFormValues>({ defaultValues });

  const today = localDate(new Date());
  const start = methods.watch('start');
  const locums = isLocums(methods.watch('employmentType'));
  const { clearErrors, getValues, setValue } = methods;
  const providerCategory = methods.watch('providerCategory');
  const specialty = methods.watch('specialty');
  const specialtyId = specialty ? Number(specialty.value) : null;
  const workLocation = methods.watch('workLocation');
  const needsLocation = takesLocation(workLocation);
  const [locationStatus, setLocationStatus] = useState<LocationLookupStatus>('short');
  const loadLocationOptions = useCallback(
    (input: string) => lookupLocations(input, setLocationStatus),
    []
  );

  // [MCV-1841] A filter that is greyed out must not keep (or send) its old value. End date
  // applies to Locums only: leaving Locums clears it, and coming back restores the default window.
  const previousLocumsRef = useRef(locums);
  useEffect(() => {
    if (previousLocumsRef.current === locums) return;
    previousLocumsRef.current = locums;
    clearErrors('end');
    setValue('end', locums ? defaultEndDate(getValues('start') || today) : '');
  }, [clearErrors, getValues, locums, setValue, today]);

  // [MCV-1863] The location box shows for On-site and Hybrid only; Any location and Remote clear it.
  useEffect(() => {
    if (!needsLocation) setValue('location', null);
  }, [needsLocation, setValue]);

  // The subspecialty list belongs to one specialty (MCV-1840): fetched when the specialty
  // changes, and any earlier pick is dropped with it.
  const [subspecialtyOptions, setSubspecialtyOptions] = useState<SubspecialtyOption[]>([]);
  const [subspecialtiesLoaded, setSubspecialtiesLoaded] = useState(false);
  // A failed lookup is not an empty one: it keeps the field disabled but says so, and Retry
  // (which bumps the counter) fetches again for the same specialty.
  const [subspecialtiesFailed, setSubspecialtiesFailed] = useState(false);
  const [subspecialtyAttempt, setSubspecialtyAttempt] = useState(0);
  const previousSpecialtyIdRef = useRef<number | null>(specialtyId);
  useEffect(() => {
    if (previousSpecialtyIdRef.current !== specialtyId) {
      previousSpecialtyIdRef.current = specialtyId;
      setValue('subspecialties', []);
    }
    setSubspecialtyOptions([]);
    setSubspecialtiesLoaded(false);
    setSubspecialtiesFailed(false);
    if (specialtyId === null) return;
    const controller = new AbortController();
    loadSubspecialtyOptions(specialtyId, controller.signal)
      .then((options) => {
        setSubspecialtyOptions(options);
        setSubspecialtiesLoaded(true);
      })
      .catch(() => {
        // An aborted request means the specialty changed; the next effect run owns the state.
        if (!controller.signal.aborted) setSubspecialtiesFailed(true);
      });
    return () => controller.abort();
  }, [specialtyId, subspecialtyAttempt, setValue]);

  // Deliberately the form element's own submit rather than RHF's
  // `handleSubmit`: that wrapper is async, and the page's request bookkeeping
  // (`loadGeneration`) is cleared from the same tick the
  // filters are applied. `getValues` reads the same store synchronously.
  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const values = methods.getValues();

    // The raw inputs this panel replaced carried `required` on both dates, so
    // an incomplete range never reached the API. Same rule, now shown as the
    // app's inline field errors instead of a native browser bubble.
    methods.clearErrors();
    let valid = true;
    if (!values.start) {
      methods.setError('start', { type: 'required', message: 'Start date is required.' });
      valid = false;
    }
    const needsEnd = isLocums(values.employmentType);
    if (needsEnd && !values.end) {
      methods.setError('end', { type: 'required', message: 'End date is required.' });
      valid = false;
    }
    // The API rejects a window that starts before today (one day of slack for
    // timezones), so say so here; the pickers also stop at today. Past dates
    // stay valid on the provider calendar, so this is not in the shared rule.
    if (values.start && values.start < today) {
      methods.setError('start', { type: 'validate', message: "Start date can't be in the past." });
      valid = false;
    }
    if (!valid) return;

    const windowError = needsEnd ? availabilityWindowError(values.start, values.end) : null;
    if (windowError) {
      methods.setError('end', { type: 'validate', message: windowError });
      return;
    }

    onApply(toFilters(values), values);
  };

  // Back to the default window (today + 30 days) with every other filter
  // blank, and re-run the search so the results match what the form shows.
  const handleClear = () => {
    const cleared = initialSearchFilters();
    methods.clearErrors();
    methods.reset(toFormValues(cleared));
    onClear(cleared);
  };

  return (
    <FormProvider {...methods}>
      <form
        className="rounded-lg border border-grey-900 bg-white p-4 shadow-sm"
        onSubmit={handleSubmit}
        // The raw <input type="date"> this replaced carried no min/max, but
        // the shared DatePicker injects min="1915-01-01" / max="2125-12-31"
        // (DatePicker.tsx:6-7). Without noValidate an out-of-range year would
        // now raise a native constraint bubble, which is exactly the
        // browser-native messaging this panel's react-hook-form errors exist
        // to replace. Validation stays with `methods` below.
        noValidate
      >
        <FormLayout>
          <FormSection componentsPerLine={4}>
            <TextInput name="query" label="Search" placeholder="Listing or specialty" />
            <Select
              name="employmentType"
              label="Employment type"
              options={EMPLOYMENT_TYPE_OPTIONS}
            />
            <AsyncSelect
              name="providerCategory"
              label="Provider type"
              cyTag="provider-search-provider-type"
              minChars={false}
              loadOptions={loadCategoryOptions}
              // The specialty list belongs to one provider type; a stale pick
              // would silently filter on the previous one.
              onChange={() => {
                methods.setValue('specialty', null);
                methods.setValue('subspecialties', []);
              }}
            />
            <AsyncSelect
              key={`specialty-${providerCategory?.value ?? 'none'}`}
              name="specialty"
              label="Specialty"
              cyTag="provider-search-specialty"
              minChars={false}
              disabled={!providerCategory}
              loadOptions={(input: string) =>
                providerCategory
                  ? loadAOIOptionByProviderCategory(Number(providerCategory.value), input)
                  : Promise.resolve([])
              }
            />
            {/* h-full: this wrapper is the grid cell's only child, so it keeps the cell's height. */}
            <div className="h-full">
              <Multiselect
                name="subspecialties"
                label="Subspecialty"
                cyTag="provider-search-subspecialty"
                options={subspecialtyOptions}
                closeMenuOnSelect={false}
                blurInputOnSelect={false}
                disabled={subspecialtyOptions.length === 0}
                placeholder={
                  subspecialtiesFailed
                    ? "Couldn't load subspecialties"
                    : specialtyId !== null &&
                        subspecialtiesLoaded &&
                        subspecialtyOptions.length === 0
                      ? 'No subspecialties for this specialty'
                      : undefined
                }
              />
              {subspecialtiesFailed && (
                <Button
                  type="button"
                  size="sm"
                  colorVariant="outline"
                  cyTag="provider-search-subspecialty-retry"
                  onClick={() => setSubspecialtyAttempt((attempt) => attempt + 1)}
                >
                  Retry
                </Button>
              )}
            </div>
            <DatePicker name="start" label="Start date" required minDate={today} />
            <DatePicker
              name="end"
              label="End date"
              required={locums}
              disabled={!locums}
              minDate={start || today}
            />
            <Select name="shiftLabel" label="Shift" options={SHIFT_LABEL_OPTIONS} />
            <Select name="shiftType" label="Shift type" options={SHIFT_TYPE_OPTIONS} />
            <Select name="workLocation" label="Work location" options={WORK_LOCATION_OPTIONS} />
            {needsLocation && (
              <AsyncSelect
                name="location"
                cyTag="provider-search-location"
                label="Location"
                placeholder="City, ST or ZIP"
                noOptionsMessage={LOCATION_MESSAGES[locationStatus]}
                loadingMessage="Searching..."
                minChars={false}
                defaultOptions={false}
                // A failed or empty lookup must not be remembered: the same query is tried again.
                cacheOptions={false}
                filterOption={null}
                loadOptions={loadLocationOptions}
              />
            )}
            <Multiselect
              name="stateCodes"
              label="License states"
              cyTag="provider-search-license-states"
              options={US_STATES_AND_TERRITORIES}
              closeMenuOnSelect={false}
              blurInputOnSelect={false}
            />
          </FormSection>
          <FormActions sticky={false}>
            <Button
              type="button"
              colorVariant="outline"
              label="Clear"
              cyTag="provider-search-clear"
              onClick={handleClear}
            />
            <Button type="submit">Search availability</Button>
          </FormActions>
        </FormLayout>
      </form>
    </FormProvider>
  );
};

export const ProviderSearchPage = () => {
  const viewerScopeKey = useSelector(UserContext.useActorRef(), selectUnmaskViewerScope);
  const navigate = useNavigate();
  return (
    <ServiceResponseBoundary viewerScopeKey={viewerScopeKey} recoveryLinks>
      <InitialInquiryFlow
        viewerScopeKey={viewerScopeKey}
        onStarted={(response) => navigate(buildServiceOpportunitiesUrl(response.id))}
      >
        {(controls) => (
          <ProviderSearchContent
            key={viewerScopeKey}
            viewerScopeKey={viewerScopeKey}
            {...controls}
            onOpenConversation={(id) => navigate(buildServiceOpportunitiesUrl(id))}
            onOpenInbox={() => navigate(paths.hlx.serviceOpportunities)}
            onViewMaskedVault={(serviceListingId) =>
              navigate(buildProviderViewUrl({ serviceListingId }))
            }
          />
        )}
      </InitialInquiryFlow>
    </ServiceResponseBoundary>
  );
};

const ProviderSearchContent = ({
  viewerScopeKey,
  openInquiry,
  inquiryBusy,
  onOpenConversation,
  onOpenInbox,
  onViewMaskedVault,
}: {
  viewerScopeKey: string;
  openInquiry: (source: InquirySource) => void;
  inquiryBusy: boolean;
  onOpenConversation: (responseId: number) => void;
  onOpenInbox: () => void;
  onViewMaskedVault: (serviceListingId: number) => void;
}) => {
  // [MCV-1882] Opening a result's masked vault unmounts this page, so the applied search is
  // kept in sessionStorage and restored here; otherwise the employer came back to blank filters.
  const [restored] = useState(() => readSearch(viewerScopeKey));
  // The panel keeps its own draft in form state; this is only the seed it
  // mounts with, so it stays fixed for the life of the page.
  const [initialDraft] = useState<ProviderSearchFormValues>(
    () => restored?.values ?? toFormValues(initialSearchFilters())
  );
  const [filters, setFilters] = useState<ProviderSearchFilters>(
    () => restored?.filters ?? initialSearchFilters()
  );
  // The form values behind `filters`, or null while the filters are the untouched defaults.
  const [appliedValues, setAppliedValues] = useState<ProviderSearchFormValues | null>(
    restored?.values ?? null
  );
  const [results, setResults] = useState<ProviderServiceListingSearchResponse | null>(null);
  const [page, setPage] = useState(restored?.page ?? 1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<number, Set<string>>>({});
  const [hasSearched, setHasSearched] = useState(restored !== null);
  const loadGeneration = useRef(0);

  // `providerAvailabilitySearch` is resolved server-side per Clerk user, and
  // for an employer (who has no provider row of their own) it only reflects
  // whether the flag is enabled *globally* -- employers can't see the
  // per-provider allowlist the backend also honors. The backend search
  // itself already applies that allowlist and returns listings from any
  // allowlisted provider even while the flag is off org-wide, so the client
  // must never use this flag to skip the request or hide the form. It's read
  // only to pick which empty-state copy to show after a real, zero-result
  // search: a flag fetch failure ('error') is treated like "unknown", not
  // like "disabled", so a transient failure doesn't get shown as a disabled
  // notice.
  const flagStatus = useFeatureFlagStatus();
  const searchEnabled = useFeatureFlag('providerAvailabilitySearch');

  const load = useCallback(async (activeFilters: ProviderSearchFilters, activePage: number) => {
    const generation = ++loadGeneration.current;
    let clamped = false;
    setLoading(true);
    setError(null);
    try {
      const response = await searchProviderServiceListings(activeFilters, activePage, PAGE_SIZE);
      if (generation !== loadGeneration.current) return;
      // A restored page (MCV-1882) can be past the end once fewer listings match; step back to
      // the last page that has results instead of showing an empty one with no way back. Loading
      // stays on until that page arrives, so neither the empty state nor stale rows flash first.
      const lastPage = Math.max(1, Math.ceil(response.total / PAGE_SIZE));
      if (activePage > lastPage) {
        clamped = true;
        setPage(lastPage);
        return;
      }
      setResults(response);
      setSelected({});
    } catch (searchError) {
      if (generation !== loadGeneration.current) return;
      setResults(null);
      setError(searchErrorMessage(searchError));
    } finally {
      if (generation === loadGeneration.current && !clamped) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(filters, page);
    return () => {
      // Invalidate a request when its filter/page effect is superseded or the
      // page unmounts, even if the underlying transport cannot be aborted.
      loadGeneration.current += 1;
    };
  }, [filters, load, page]);

  // The untouched default search is worth keeping only once the employer has paged through it.
  useEffect(() => {
    if (appliedValues || page > 1) {
      writeSearch(viewerScopeKey, {
        values: appliedValues ?? toFormValues(filters),
        filters,
        page,
      });
    } else {
      clearSearch(viewerScopeKey);
    }
  }, [appliedValues, filters, page, viewerScopeKey]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((results?.total ?? 0) / PAGE_SIZE)),
    [results?.total]
  );

  const toggleOption = (listingId: number, option: PublicAvailabilitySelectionOption) => {
    const key = selectionOptionKey(option);
    setSelected((current) => {
      const nextForListing = new Set(current[listingId] ?? []);
      if (nextForListing.has(key)) nextForListing.delete(key);
      else nextForListing.add(key);
      return { ...current, [listingId]: nextForListing };
    });
  };

  const showRolloutCopy = hasSearched && !error && flagStatus === 'ready' && !searchEnabled;

  return (
    <div className="space-y-4 text-left" aria-busy={loading}>
      <header>
        <h1 className="text-2xl font-semibold text-content-primary">Search Providers</h1>
        <p className="mt-1 text-sm text-content-secondary">
          Find public service listings whose published availability matches your dates.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button
            type="button"
            colorVariant="outline"
            label="View provider responses"
            onClick={onOpenInbox}
          />
          <TutorialVideoButton videoId="searchProviders" tooltipAlign="start" />
        </div>
      </header>
      <SearchFilters
        defaultValues={initialDraft}
        onApply={(draftFilters, draftValues) => {
          setHasSearched(true);
          setPage(1);
          setFilters(draftFilters);
          setAppliedValues(draftValues);
        }}
        onClear={(clearedFilters) => {
          // The cleared defaults are not a search worth coming back to; the effect above drops it.
          setAppliedValues(null);
          setHasSearched(true);
          setPage(1);
          setFilters(clearedFilters);
        }}
      />
      {error && (
        <p
          className="rounded-md border border-error/30 bg-error/5 p-3 text-sm text-error"
          role="alert"
        >
          {error}
        </p>
      )}
      {loading ? (
        <p
          className="rounded-lg border border-grey-900 bg-white p-6 text-content-secondary"
          role="status"
        >
          Searching provider availability…
        </p>
      ) : results?.data.length ? (
        <div className="min-w-0 lg:overflow-x-auto lg:rounded-lg lg:border lg:border-grey-900 lg:bg-white">
          <table
            className="block w-full text-left text-sm lg:table lg:table-fixed"
            aria-label="Provider search results"
          >
            <thead className="hidden border-b border-grey-900 bg-grey-950 lg:table-header-group">
              <tr>
                <th scope="col" className="w-[28%] p-4">
                  Listing
                </th>
                <th scope="col" className="w-[16%] p-4">
                  Work location
                </th>
                <th scope="col" className="w-[12%] p-4">
                  Licensed states
                </th>
                <th scope="col" className="w-[20%] p-4">
                  Availability
                </th>
                <th scope="col" className="w-[24%] p-4">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="block space-y-4 lg:table-row-group lg:space-y-0">
              {results.data.map((listing) => {
                // Part-time and full-time providers are offered by start date (MCV-1837): a single
                // option on the earliest date is selected for them; several overlapping ones are
                // a required choice between just those options (StartOptionChoice), with no
                // calendar. Begin negotiation sends exactly the selected option.
                const startDateOnly = isStartDateOnly(listing.listingKind);
                const startOptions = earliestDateOptions(
                  listing.availability?.selectionOptions ?? []
                );
                const selectedKeys = startDateOnly
                  ? startOptions.length === 1
                    ? new Set(autoSelectedStartKeys(startOptions))
                    : new Set(
                        [...(selected[listing.providerServiceListingId] ?? [])].filter((key) =>
                          startOptions.some((option) => selectionOptionKey(option) === key)
                        )
                      )
                  : (selected[listing.providerServiceListingId] ?? new Set<string>());
                const entryIds = selectedAvailabilityEntryIds(listing, selectedKeys);
                const startDate = startDateOnly ? startOptions[0]?.availabilityDate : undefined;
                return (
                  <ServiceDiscoveryBeginFlow
                    key={listing.providerServiceListingId}
                    listing={listing}
                    selectedKeys={[...selectedKeys]}
                    onConfirmed={onOpenConversation}
                    onResetSelection={() => {
                      setSelected((previous) => ({
                        ...previous,
                        [listing.providerServiceListingId]: new Set(),
                      }));
                      void load(filters, page);
                    }}
                  >
                    {({ busy, begin, confirmedResponseId, notice }) => {
                      const responseId = listing.existingResponseId ?? confirmedResponseId;
                      return (
                        <tr className="block rounded-lg border border-grey-900 bg-white p-5 shadow-sm lg:table-row lg:rounded-none lg:border-x-0 lg:border-t-0 lg:p-0 lg:shadow-none">
                          <td className="block align-top [overflow-wrap:anywhere] lg:table-cell lg:p-4">
                            <h2 className="font-semibold text-content-primary">
                              {listing.listingTitle}
                            </h2>
                            <p className="mt-1 text-content-secondary">
                              {[listing.providerCategoryCode, ...listing.specialties]
                                .filter(Boolean)
                                .join(' · ') || 'Healthcare provider'}
                            </p>
                            {listing.subspecialties.length > 0 && (
                              <p className="mt-1 text-content-secondary">
                                <span className="sr-only">Subspecialties: </span>
                                {listing.subspecialties.join(' · ')}
                                {!listing.generalSpecialtyFlag && (
                                  <span className="mt-1 block text-content-tertiary">
                                    Subspecialty work only
                                  </span>
                                )}
                              </p>
                            )}
                            <p className="mt-2 text-content-secondary">
                              <span className="sr-only">Listing type: </span>
                              {formatKind(listing.listingKind as ListingKind)}
                            </p>
                          </td>
                          <td className="mt-3 block align-top [overflow-wrap:anywhere] lg:table-cell lg:p-4">
                            <span className="block text-content-tertiary lg:hidden">
                              Work location
                            </span>
                            {formatWorkLocation(listing.workLocation)}
                          </td>
                          <td className="mt-3 block align-top [overflow-wrap:anywhere] lg:table-cell lg:p-4">
                            <span className="block text-content-tertiary lg:hidden">
                              Licensed states
                            </span>
                            {listing.licenseStateCodes.join(', ') || 'Not specified'}
                          </td>
                          <td className="mt-3 block align-top lg:table-cell lg:p-4">
                            {startDateOnly ? (
                              <p>
                                Start date: {startDate ? formatDate(startDate) : 'Not specified'}
                              </p>
                            ) : (
                              <p>
                                {listing.availability?.matchingDateCount ?? 0} available{' '}
                                {listing.availability?.matchingDateCount === 1 ? 'date' : 'dates'}
                              </p>
                            )}
                            <span className="mt-1 inline-block rounded-full bg-primary-t-900 px-2.5 py-1 text-xs font-semibold text-primary-50">
                              {listing.availability?.dateMatch === 'exact'
                                ? 'Exact shift match'
                                : 'Date match'}
                            </span>
                            {responseId == null && startDateOnly && (
                              <StartOptionChoice
                                listing={listing}
                                selectedKeys={selectedKeys}
                                onSelect={(option) =>
                                  setSelected((current) => ({
                                    ...current,
                                    [listing.providerServiceListingId]: new Set([
                                      selectionOptionKey(option),
                                    ]),
                                  }))
                                }
                                disabled={inquiryBusy || busy}
                              />
                            )}
                            {responseId == null && !startDateOnly && (
                              <ProviderAvailabilityOptions
                                listing={listing}
                                previewLimit={CARD_DATE_LIMIT}
                                previewMode="dates"
                                desktopDialog
                                selectedKeys={selectedKeys}
                                onToggle={(option) =>
                                  toggleOption(listing.providerServiceListingId, option)
                                }
                                disabled={inquiryBusy || busy}
                              />
                            )}
                          </td>
                          <td className="mt-4 block align-top lg:table-cell lg:p-4">
                            <div className="flex flex-col gap-3">
                              <Button
                                type="button"
                                wrap
                                colorVariant="outline"
                                cyTag={`provider-search-view-vault-${listing.providerServiceListingId}`}
                                onClick={() => onViewMaskedVault(listing.providerServiceListingId)}
                              >
                                View masked vault
                              </Button>
                              {responseId != null ? (
                                <Button
                                  type="button"
                                  wrap
                                  label="Open provider response"
                                  onClick={() => onOpenConversation(responseId)}
                                />
                              ) : (
                                <>
                                  <Button
                                    type="button"
                                    wrap
                                    colorVariant="outline"
                                    disabled={inquiryBusy || busy}
                                    onClick={() =>
                                      openInquiry({
                                        target: {
                                          listingType: 'SERVICE',
                                          providerServiceListingId:
                                            listing.providerServiceListingId,
                                        },
                                        title: listing.listingTitle,
                                      })
                                    }
                                  >
                                    Message
                                  </Button>
                                  <Button
                                    wrap
                                    disabled={entryIds.length === 0 || busy || inquiryBusy}
                                    type="button"
                                    onClick={begin}
                                  >
                                    Begin negotiation
                                  </Button>
                                </>
                              )}
                            </div>
                            {notice}
                          </td>
                        </tr>
                      );
                    }}
                  </ServiceDiscoveryBeginFlow>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        hasSearched &&
        !error && (
          <p className="rounded-lg border border-grey-900 bg-white p-6 text-content-secondary">
            No public provider availability matched these filters.
            {showRolloutCopy && (
              <span className="block mt-1">
                Availability-based provider search is still rolling out, so only providers enabled
                for it can appear here.
              </span>
            )}
          </p>
        )
      )}
      {!loading && (results?.total ?? 0) > PAGE_SIZE && (
        <nav className="flex items-center justify-end gap-3" aria-label="Provider search pages">
          <Button
            colorVariant="outline"
            size="sm"
            disabled={page <= 1}
            type="button"
            onClick={() => setPage((current) => current - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-content-secondary">
            Page {page} of {totalPages}
          </span>
          <Button
            colorVariant="outline"
            size="sm"
            disabled={page >= totalPages}
            type="button"
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </Button>
        </nav>
      )}
    </div>
  );
};
